import {ImportantNotes} from '../components/PracticeNotes';
import PracticeDocumentsArchive from '../components/PracticeDocumentsArchive';
import React, { useEffect, useState } from 'react';
import { FaPlus, FaCalendarAlt } from 'react-icons/fa';
import ContactCard from '../components/ContactCard';

const titleCase = value => String(value || '').toLocaleLowerCase('it-IT').replace(/(^|[\s'’-])([\p{L}])/gu,
  (_match, separator, letter) => separator + letter.toLocaleUpperCase('it-IT'));
const phoneHref = value => `tel:${String(value).replace(/[^+0-9]/g, '')}`;
const blank = { titolo: '', nome: '', telefono: '', email: '', indirizzo: '' };
export default function PraticaRiepilogo({ schedaId, tipo, cliente = {}, dannoAuto = {}, refreshKey, onStatusSaved, onDirtyChange, onLinksSaved, editingConflict, onNewNote, onNewDeadline, importantNotes = [], operator, showStatus = true }) {
  const [referentsForCommunication, setReferentsForCommunication] = useState([]);
  useEffect(() => {
    if (!schedaId) return;
    let active = true;
    setReferentsForCommunication([]);
    fetch(`/api/pratica-contatti/scheda/${schedaId}`).then(async response => {
      if (!response.ok) throw new Error('Referenti non disponibili');
      return response.json();
    }).then(rows => { if (active) setReferentsForCommunication([...new Map(rows.filter(item => item.dati_ruolo?.referenteComunicazioni && item.origine === 'manuale' && item.tipo_soggetto === 'persona').map(item => [String(item.contatto_id),item])).values()]); })
      .catch(cause => { if (active) setError(cause.message); });
    return () => { active = false; };
  }, [schedaId, refreshKey]);
  const [moreActions,setMoreActions]=useState(false);
  const [summary, setSummary] = useState(null);
  const [states, setStates] = useState([]);
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [referents, setReferents] = useState([]);
  const [contact, setContact] = useState(blank);
  const [isNew, setIsNew] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalBusy, setModalBusy] = useState(false);
  useEffect(() => { onDirtyChange?.(Boolean(summary && selected !== summary.stato)); }, [summary, selected, onDirtyChange]);
  const refresh = async () => {
    const response = await fetch(`/api/pratica-riepilogo/${schedaId}`);
    if (!response.ok) throw new Error('Riepilogo non disponibile');
    const data = await response.json();
    setSummary(data); setSelected(data.stato); setError('');
    return data;
  };
  useEffect(() => {
    if (!schedaId) return;
    let active = true;
    Promise.all([
      fetch(`/api/pratica-riepilogo/${schedaId}`).then(r => r.ok ? r.json() : Promise.reject(new Error('Riepilogo non disponibile'))),
      fetch(`/api/workflow/stati?tipo=${encodeURIComponent(tipo || '')}`).then(r => r.ok ? r.json() : Promise.reject(new Error('Stati non disponibili')))
    ]).then(([data, values]) => { if (active) { setSummary(data); setSelected(data.stato); setStates(values); setError(''); } })
      .catch(cause => { if (active) setError(cause.message); });
    return () => { active = false; };
  }, [schedaId, refreshKey, tipo]);
  useEffect(() => {
    if (!modal || !form?.compagniaId) { setReferents([]); return; }
    let active = true;
    const role = modal === 'compagnia' ? 'Ispettorato' : modal;
    fetch(`/api/compagnie-assicurative/${form.compagniaId}/referenti?ruolo=${role}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Referenti non disponibili')))
      .then(rows => { if (active) setReferents(rows); })
      .catch(cause => { if (active) setModalError(cause.message); });
    return () => { active = false; };
  }, [modal, form?.compagniaId]);
  useEffect(() => {
    if (!modal || !form?.contactId || isNew) { setContact(blank); return; }
    let active = true;
    fetch(`/api/contatti/${form.contactId}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Contatto non disponibile')))
      .then(data => { if (active) setContact({ titolo: data.titolo || '', nome: titleCase(data.nome), telefono: data.telefoni?.[0]?.telefono || '',
        email: data.emails?.[0]?.email || '', indirizzo: data.indirizzo || '' }); })
      .catch(cause => { if (active) setModalError(cause.message); });
    return () => { active = false; };
  }, [modal, form?.contactId, isNew]);

  const saveStatus = async () => {
    if (!selected || selected === summary?.stato || busy) return;
    setBusy(true); setError('');
    try {
      const response = await fetch(`/api/workflow/schede/${schedaId}/stato`, { method: 'PUT',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stato: selected }) });
      if (!response.ok) throw new Error('Stato non salvato');
      const saved = await response.json();
      setSummary(previous => ({ ...previous, stato: saved.stato })); onStatusSaved?.(saved.stato);
    } catch (cause) { setError(cause.message); } finally { setBusy(false); }
  };
  const openModal = async role => {
    if (!summary) return;
    setModal(role); setModalError(''); setIsNew(false);
    setForm({ compagniaId: summary.compagnia_id || null, numeroSinistro: summary?.numero_sinistro || '',
      liquidatoreId: summary.dettagli_compagnia?.liquidatoreId || null,
      ispettoratoId: summary.dettagli_compagnia?.ispettoratoId || null,
      contactId: role === 'compagnia' ? summary.compagnia?.id : role === 'Liquidatore' ? summary.liquidatore?.id : summary.ispettorato?.id });
    try {
      const response = await fetch('/api/compagnie-assicurative');
      if (!response.ok) throw new Error('Compagnie non disponibili');
      setCompanies(await response.json());
    } catch (cause) { setModalError(cause.message); }
  };
  const saveModal = async event => {
    event.preventDefault();
    setModalBusy(true); setModalError('');
    try {
      const next = { ...form };
      if (isNew) {
        if (!next.compagniaId || !contact.nome.trim()) throw new Error('Indica la compagnia e il nome del referente.');
        const response = await fetch(`/api/compagnie-assicurative/${next.compagniaId}/referenti`, { method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ruolo: modal, nome: titleCase(contact.nome.trim()), telefono: contact.telefono.trim() }) });
        const created = await response.json();
        if (!response.ok) throw new Error(created.error || 'Referente non creato');
        next.contactId = created.id;
        next[modal === 'Liquidatore' ? 'liquidatoreId' : 'ispettoratoId'] = created.id;
      }
      if (next.contactId && (modal !== 'compagnia' || next.compagniaId === summary.compagnia_id)) {
        const response = await fetch(`/api/contatti/${next.contactId}/recapiti`, { method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...contact, nome: titleCase(contact.nome.trim()) }) });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Contatto non salvato');
      }
      const response = await fetch(`/api/pratica-riepilogo/${schedaId}/collegamenti`, { method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ compagniaId: next.compagniaId, numeroSinistro: next.numeroSinistro,
          liquidatoreId: next.liquidatoreId, ispettoratoId: next.ispettoratoId }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Collegamenti non salvati');
      const updated = await refresh(); onLinksSaved?.(updated); setModal(null);
    } catch (cause) { setModalError(cause.message); } finally { setModalBusy(false); }
  };

  if (!schedaId) return <aside className="practice-rail"><div className="practice-side-card"><h2>Riepilogo pratica</h2><p>Salva la pratica per vedere stato e contatti.</p></div></aside>;
  const visibleStates = tipo === 'Auto' ? states.filter(value => !['MEDICO LEGALE DI PARTE', 'MEDICO LEGALE DI CONTROPARTE'].includes(value)) : states;
  const label = role => role === 'compagnia' ? 'Compagnia' : role;
  return <aside className="practice-rail" aria-label="Riepilogo pratica">
    {showStatus && <section className="practice-side-card practice-status-card">
      <div className="practice-side-label">AVANZAMENTO</div><h2>Stato pratica</h2>
      <select className="practice-status-current" id="practice-status-select" aria-label="Stato pratica" value={selected} disabled={!summary || busy} onChange={event => setSelected(event.target.value)}>
        {visibleStates.map(value => <option key={value} value={value}>{value}</option>)}
      </select>
      <button type="button" className="practice-status-save" disabled={!summary || busy || selected === summary.stato} onClick={saveStatus}>
        {busy ? 'Salvataggio…' : 'Salva stato'}
      </button>
      {selected !== summary?.stato && <small>Il nuovo stato sarà registrato nello storico.</small>}
    </section>}
    <div className="practice-quick-create"><button type="button" disabled={!schedaId} onClick={onNewNote}><FaPlus/><span>Nuova nota</span></button><button type="button" disabled={!schedaId} onClick={onNewDeadline}><FaCalendarAlt/><span>Scadenza</span></button><button type="button" className="rail-more-toggle" aria-expanded={moreActions} onClick={()=>setMoreActions(v=>!v)}>•••<span>{moreActions?'Chiudi':'Altre azioni'}</span></button></div>
    <div className={'practice-contact-buttons'+(moreActions?' rail-more-open':'')}><ImportantNotes notes={importantNotes}/>
      <ContactCard emailSubject={[cliente.nome,summary?.numero_sinistro ? "Sinistro n. "+summary?.numero_sinistro : "Sinistro n. [da completare]"].filter(Boolean).join(" — ")} compact actionOnly role="Cliente" name="Contatta il cliente" contact={cliente} detailTo={`/pratica/${schedaId}?sezione=datiCliente`} />
      {referentsForCommunication.map(item => <ContactCard key={item.contatto_id} compact role="Referente" name={item.nome} contact={item} emailSubject={[cliente.nome,summary?.numero_sinistro ? 'Sinistro n. ' + summary.numero_sinistro : 'Sinistro n. [da completare]'].filter(Boolean).join(' — ')} detailTo={`/pratica/${schedaId}?sezione=altreAnagrafiche`}/>)}
      <ContactCard emailSubject={[cliente.nome,summary?.numero_sinistro ? "Sinistro n. "+summary?.numero_sinistro : "Sinistro n. [da completare]"].filter(Boolean).join(" — ")} compact role="Compagnia" contact={summary?.compagnia || {}} name={summary?.nome_compagnia} onOpen={() => openModal('compagnia')} />
      <ContactCard emailSubject={[cliente.nome,summary?.numero_sinistro ? "Sinistro n. "+summary?.numero_sinistro : "Sinistro n. [da completare]"].filter(Boolean).join(" — ")} compact role="Liquidatore" contact={summary?.liquidatore || {}} onOpen={() => openModal('Liquidatore')} />
      <PracticeDocumentsArchive schedaId={schedaId} tipo={tipo} dannoAuto={dannoAuto} authorityPresent={Boolean(summary?.autorita_intervenuta)} operator={operator} refreshKey={refreshKey} />
    </div>

    {error && <p role="alert" className="practice-side-error">{error}</p>}
    {modal && form && <div className="practice-modal-overlay" role="presentation">
      <form className="practice-modal" role="dialog" aria-modal="true" aria-labelledby="practice-modal-title" onSubmit={saveModal}>
        <div className="practice-modal-header"><div><span className="practice-section-eyebrow">PRATICA {summary?.numero_progressivo}</span>
          <h2 id="practice-modal-title">{label(modal)}</h2></div>
          <button type="button" className="practice-modal-close" aria-label="Chiudi finestra" onClick={() => setModal(null)}>×</button></div>
        {modal === 'compagnia' ? <>
          <label>Compagnia collegata<select value={form.compagniaId || ''} onChange={event => setForm(previous => ({ ...previous,
            compagniaId: event.target.value ? Number(event.target.value) : null, liquidatoreId: null, ispettoratoId: null,
            contactId: Number(event.target.value) === Number(summary.compagnia_id) ? summary.compagnia?.id : null }))}>
            <option value="">Nessuna compagnia</option>
            {companies.map(company => <option key={company.id} value={company.id}>{company.nome}</option>)}
          </select></label>
          <label>Numero sinistro<input value={form.numeroSinistro} onChange={event => setForm(previous => ({ ...previous, numeroSinistro: event.target.value }))} /></label>
          {form.compagniaId && <label>Ispettorato di riferimento<select value={form.ispettoratoId || ''} onChange={event => setForm(previous => ({ ...previous, ispettoratoId: event.target.value ? Number(event.target.value) : null }))}>
            <option value="">Nessun ispettorato</option>
            {referents.map(item => <option key={item.id} value={item.id}>{item.nome}</option>)}
          </select></label>}
          {form.compagniaId && form.compagniaId !== summary.compagnia_id && <p>Dopo il salvataggio potrai riaprire il pulsante per modificare i recapiti della nuova compagnia.</p>}
        </> : <>
          {!form.compagniaId && <p>Collega prima una compagnia alla pratica.</p>}
          {form.compagniaId && <label>{label(modal)} collegato<select value={isNew ? '__new__' : form.contactId || ''}
            onChange={event => { const value = event.target.value; setIsNew(value === '__new__'); setContact(blank);
              setForm(previous => ({ ...previous, contactId: value && value !== '__new__' ? Number(value) : null,
                [modal === 'Liquidatore' ? 'liquidatoreId' : 'ispettoratoId']: value && value !== '__new__' ? Number(value) : null })); }}>
            <option value="">Nessuno</option>
            {referents.map(item => <option key={item.id} value={item.id}>{item.nome}</option>)}
            <option value="__new__">+ Nuovo {modal.toLocaleLowerCase('it-IT')}</option>
          </select></label>}
        </>}
        {(form.contactId || isNew) && <div className="practice-modal-fields"><h3>{isNew ? 'Nuovo contatto' : 'Dati del contatto'}</h3>
          <label>Nome<input value={contact.nome} required onChange={event => setContact(previous => ({ ...previous, nome: event.target.value }))} /></label>
          {modal === 'Liquidatore' && <label>Titolo<select value={contact.titolo || ''} onChange={event => setContact(previous => ({ ...previous, titolo: event.target.value }))}><option value="">Da indicare</option><option value="Dott.">Dott.</option><option value="Dott.ssa">Dott.ssa</option></select></label>}<label>Telefono<input type="tel" value={contact.telefono} onChange={event => setContact(previous => ({ ...previous, telefono: event.target.value }))} /></label>
          <label>Email<input type="email" value={contact.email} onChange={event => setContact(previous => ({ ...previous, email: event.target.value.toLocaleLowerCase('it-IT') }))} /></label>
          <label>Indirizzo<input value={contact.indirizzo} onChange={event => setContact(previous => ({ ...previous, indirizzo: event.target.value }))} /></label>
          {(contact.telefono || contact.email) && <div className="practice-modal-links">
            {contact.telefono && <a href={phoneHref(contact.telefono)}>Chiama {contact.telefono}</a>}
            {contact.email && <a href={`mailto:${contact.email}?subject=${encodeURIComponent([cliente.nome,summary?.numero_sinistro ? "Sinistro n. "+summary?.numero_sinistro : "Sinistro n. [da completare]"].filter(Boolean).join(" — "))}`}>Scrivi email</a>}
          </div>}</div>}
        {modalError && <p role="alert" className="practice-side-error">{modalError}</p>}
        <div className="practice-modal-actions"><button type="button" onClick={() => setModal(null)}>Annulla</button>
          <button type="submit" disabled={modalBusy}>{modalBusy ? 'Salvataggio…' : 'Salva'}</button></div>
      </form>
    </div>}

  </aside>;
}
